<script lang="ts">
	import { page } from '$app/stores';
	import ScrollReveal from '$lib/components/ScrollReveal.svelte';
	import GlowCard from '$lib/components/GlowCard.svelte';
	import MagneticButton from '$lib/components/MagneticButton.svelte';
	import { t, type Language } from '$lib/i18n';

	let lang = $derived($page.params.lang as Language);
	let tr = $derived(t(lang));

	const features = [
		{
			titleKey: 'analytics_feature_realtime_title',
			descKey: 'analytics_feature_realtime_desc',
			icon: '<svg aria-hidden="true" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>'
		},
		{
			titleKey: 'analytics_feature_ai_title',
			descKey: 'analytics_feature_ai_desc',
			icon: '<svg aria-hidden="true" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/></svg>'
		},
		{
			titleKey: 'analytics_feature_oee_title',
			descKey: 'analytics_feature_oee_desc',
			icon: '<svg aria-hidden="true" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>'
		},
		{
			titleKey: 'analytics_feature_alerts_title',
			descKey: 'analytics_feature_alerts_desc',
			icon: '<svg aria-hidden="true" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/></svg>'
		},
		{
			titleKey: 'analytics_feature_maintenance_title',
			descKey: 'analytics_feature_maintenance_desc',
			icon: '<svg aria-hidden="true" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>'
		},
		{
			titleKey: 'analytics_feature_api_title',
			descKey: 'analytics_feature_api_desc',
			icon: '<svg aria-hidden="true" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>'
		}
	];

	const techStack = [
		{ name: 'React', categoryKey: 'analytics_tech_frontend' },
		{ name: 'Next.js', categoryKey: 'analytics_tech_frontend' },
		{ name: 'TypeScript', categoryKey: 'analytics_tech_frontend' },
		{ name: 'Python', categoryKey: 'analytics_tech_backend' },
		{ name: 'FastAPI', categoryKey: 'analytics_tech_backend' },
		{ name: 'PostgreSQL', categoryKey: 'analytics_tech_database' },
		{ name: 'TimescaleDB', categoryKey: 'analytics_tech_database' },
		{ name: 'Docker', categoryKey: 'analytics_tech_infrastructure' }
	];

	const integrationKeys = [
		'analytics_integration_opcua',
		'analytics_integration_modbus',
		'analytics_integration_mqtt',
		'analytics_integration_rest',
		'analytics_integration_scada',
		'analytics_integration_plc',
		'analytics_integration_csv',
		'analytics_integration_db'
	];
</script>

<svelte:head>
	<title>{tr('analytics_page_title')}</title>
	<meta name="description" content={tr('analytics_page_desc')} />
</svelte:head>

<!-- Hero + Features wrapper for seamless aurora -->
<div class="relative overflow-hidden">
	<!-- Aurora borealis effect spanning both sections -->
	<div class="aurora-container">
		<div class="aurora-layer aurora-1"></div>
		<div class="aurora-layer aurora-2"></div>
		<div class="aurora-layer aurora-3"></div>
	</div>

	<!-- Hero -->
	<section class="relative pt-24 md:pt-32 pb-12 md:pb-20">
		<div
			class="absolute inset-0 bg-gradient-to-b from-ameta-green/5 via-transparent to-transparent"
		></div>
		<div class="absolute inset-0 bg-grid opacity-20"></div>

		<div class="relative max-w-7xl mx-auto px-6">
			<div class="lg:grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
				<ScrollReveal>
					<div>
						<div
							class="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ameta-green/10 border border-ameta-green/30 mb-6"
						>
							<svg
								aria-hidden="true"
								class="w-4 h-4 text-ameta-green"
								fill="currentColor"
								viewBox="0 0 20 20"
							>
								<path
									fill-rule="evenodd"
									d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
									clip-rule="evenodd"
								/>
							</svg>
							<span class="text-sm font-medium text-ameta-green">{tr('analytics_page_badge')}</span>
						</div>

						<h1 class="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
							{tr('analytics_page_heading')}
						</h1>

						<p class="text-xl md:text-2xl text-slate-400 leading-relaxed mb-10">
							{tr('analytics_page_subtitle')}
						</p>

						<MagneticButton
							href="/{lang}/#contact"
							class="btn-primary text-lg inline-flex items-center gap-2"
						>
							<span>{tr('analytics_page_demo_cta')}</span>
							<svg
								aria-hidden="true"
								class="w-5 h-5"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M17 8l4 4m0 0l-4 4m4-4H3"
								/>
							</svg>
						</MagneticButton>
					</div>
				</ScrollReveal>

				<!-- Data flow diagram -->
				<ScrollReveal delay={150}>
					<div class="hidden lg:block relative">
						<div class="absolute -inset-4 bg-ameta-green/5 rounded-3xl blur-2xl"></div>
						<div class="relative bg-slate-800/50 rounded-2xl border border-slate-700 p-8">
							<!-- Input sources -->
							<div class="mb-6">
								<p class="text-xs text-slate-500 uppercase tracking-wider mb-3">
									Your data sources
								</p>
								<div class="grid grid-cols-4 gap-2">
									<span
										class="px-3 py-1.5 rounded-lg bg-slate-700/50 text-sm text-slate-300 text-center"
										>OPC-UA</span
									>
									<span
										class="px-3 py-1.5 rounded-lg bg-slate-700/50 text-sm text-slate-300 text-center"
										>SQL</span
									>
									<span
										class="px-3 py-1.5 rounded-lg bg-slate-700/50 text-sm text-slate-300 text-center"
										>Excel</span
									>
									<span
										class="px-3 py-1.5 rounded-lg bg-slate-700/50 text-sm text-slate-300 text-center"
										>ERP</span
									>
									<span
										class="px-3 py-1.5 rounded-lg bg-slate-700/50 text-sm text-slate-300 text-center"
										>SCADA</span
									>
									<span
										class="px-3 py-1.5 rounded-lg bg-slate-700/50 text-sm text-slate-300 text-center"
										>Modbus</span
									>
									<span
										class="px-3 py-1.5 rounded-lg bg-slate-700/50 text-sm text-slate-300 text-center"
										>REST</span
									>
									<span
										class="px-3 py-1.5 rounded-lg bg-slate-700/30 border border-dashed border-slate-600 text-sm text-slate-400 text-center italic"
										>whatever</span
									>
								</div>
							</div>

							<!-- Arrow down -->
							<div class="flex justify-center my-4">
								<svg
									class="w-6 h-6 text-ameta-green"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M19 14l-7 7m0 0l-7-7m7 7V3"
									/>
								</svg>
							</div>

							<!-- Platform -->
							<div class="p-4 rounded-xl bg-ameta-green/10 border border-ameta-green/30 mb-4">
								<p class="text-center font-semibold text-ameta-green">Ameta Analytics</p>
							</div>

							<!-- Arrow down -->
							<div class="flex justify-center my-4">
								<svg
									class="w-6 h-6 text-ameta-green"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M19 14l-7 7m0 0l-7-7m7 7V3"
									/>
								</svg>
							</div>

							<!-- Output -->
							<div>
								<p class="text-xs text-slate-500 uppercase tracking-wider mb-3">What you get</p>
								<div class="grid grid-cols-3 gap-3">
									<div
										class="col-span-2 p-4 rounded-xl bg-ameta-green/10 border border-ameta-green/30"
									>
										<p class="font-semibold text-ameta-green mb-2">Dashboards</p>
										<div class="flex flex-wrap gap-1.5">
											<span class="px-2 py-1 rounded bg-slate-700/50 text-xs text-slate-300"
												>OEE</span
											>
											<span class="px-2 py-1 rounded bg-slate-700/50 text-xs text-slate-300"
												>Alerts</span
											>
											<span class="px-2 py-1 rounded bg-slate-700/50 text-xs text-slate-300"
												>Reports</span
											>
											<span class="px-2 py-1 rounded bg-slate-700/50 text-xs text-slate-300"
												>Monitoring</span
											>
											<span class="px-2 py-1 rounded bg-slate-700/50 text-xs text-slate-300"
												>Maintenance</span
											>
											<span class="px-2 py-1 rounded bg-slate-700/50 text-xs text-slate-300"
												>Trends</span
											>
										</div>
									</div>
									<div class="p-4 rounded-xl bg-slate-700/30 border border-dashed border-slate-600">
										<p class="font-semibold text-slate-300 mb-2">API</p>
										<p class="text-xs text-slate-400">For your own apps</p>
									</div>
								</div>
							</div>
						</div>
					</div>
				</ScrollReveal>
			</div>
		</div>
	</section>

	<!-- Features Grid -->
	<section class="py-12 md:py-20 relative">
		<div class="max-w-7xl mx-auto px-6">
			<ScrollReveal>
				<div class="text-center max-w-3xl mx-auto mb-10 md:mb-16">
					<p class="text-ameta-green font-medium mb-4 tracking-wide">
						{tr('analytics_features_label')}
					</p>
					<h2 class="text-3xl md:text-4xl font-bold mb-6">
						{tr('analytics_features_title')}
					</h2>
					<p class="text-lg text-slate-400">
						{tr('analytics_features_subtitle')}
					</p>
				</div>
			</ScrollReveal>

			<div class="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
				{#each features as feature, i}
					<ScrollReveal delay={i * 80}>
						<GlowCard class="h-full">
							<div class="p-6 h-full flex flex-col">
								<div
									class="w-12 h-12 rounded-xl bg-ameta-green/10 flex items-center justify-center text-ameta-green mb-4"
								>
									{@html feature.icon}
								</div>
								<h3 class="text-lg font-semibold text-slate-100 mb-2">{tr(feature.titleKey)}</h3>
								<p class="text-slate-400 text-sm leading-relaxed flex-grow">
									{tr(feature.descKey)}
								</p>
							</div>
						</GlowCard>
					</ScrollReveal>
				{/each}
			</div>
		</div>
	</section>
</div>

<!-- Integrations -->
<section class="py-12 md:py-20 relative section-gradient-reverse">
	<div class="max-w-7xl mx-auto px-6">
		<div class="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
			<ScrollReveal>
				<p class="text-ameta-green font-medium mb-4 tracking-wide">
					{tr('analytics_integrations_label')}
				</p>
				<h2 class="text-3xl md:text-4xl font-bold mb-6">
					{tr('analytics_integrations_title')}
				</h2>
				<p class="text-lg text-slate-400 leading-relaxed mb-8">
					{tr('analytics_integrations_desc')}
				</p>

				<div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
					{#each integrationKeys as integrationKey}
						<div
							class="flex items-center gap-3 p-3 rounded-lg bg-slate-800/30 border border-slate-700/30"
						>
							<svg
								aria-hidden="true"
								class="w-4 h-4 text-ameta-green flex-shrink-0"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
								/>
							</svg>
							<span class="text-sm text-slate-300">{tr(integrationKey)}</span>
						</div>
					{/each}
				</div>
			</ScrollReveal>

			<ScrollReveal delay={150}>
				<div class="relative">
					<div class="absolute inset-0 bg-ameta-green/10 rounded-3xl blur-3xl"></div>
					<div
						class="relative bg-slate-800/50 rounded-2xl border border-slate-700 p-4 sm:p-8 overflow-hidden"
					>
						<p class="text-sm text-slate-500 font-mono mb-4">{tr('analytics_code_comment')}</p>
						<pre
							class="text-xs sm:text-sm text-slate-300 font-mono overflow-x-auto max-w-full"><code
								>{`from ameta import DataSource

source = DataSource(
    type="opcua",
    endpoint="opc.tcp://plc-01:4840",
    nodes=["Temperature", "Vibration"]
)

source.subscribe(
    callback=process_reading,
    interval_ms=1000
)`}</code
							></pre>
					</div>
				</div>
			</ScrollReveal>
		</div>
	</div>
</section>

<!-- Tech Stack -->
<section class="py-12 md:py-20 relative section-gradient-reverse">
	<div class="max-w-7xl mx-auto px-6">
		<ScrollReveal>
			<div class="text-center max-w-3xl mx-auto mb-8 md:mb-12">
				<p class="text-ameta-green font-medium mb-4 tracking-wide">{tr('analytics_tech_label')}</p>
				<h2 class="text-3xl md:text-4xl font-bold mb-6">
					{tr('analytics_tech_title')}
				</h2>
			</div>
		</ScrollReveal>

		<ScrollReveal delay={100}>
			<div class="flex flex-wrap justify-center gap-4">
				{#each techStack as tech}
					<div
						class="px-5 py-3 rounded-xl bg-slate-800/50 border border-slate-700/50 hover:border-ameta-green/30 transition-colors"
					>
						<p class="text-sm font-medium text-slate-200">{tech.name}</p>
						<p class="text-xs text-slate-500">{tr(tech.categoryKey)}</p>
					</div>
				{/each}
			</div>
		</ScrollReveal>
	</div>
</section>

<!-- CTA -->
<section class="py-12 md:py-20 relative">
	<div class="max-w-4xl mx-auto px-6 text-center">
		<ScrollReveal>
			<h2 class="text-3xl md:text-4xl font-bold mb-6">
				{tr('analytics_cta_title')}
			</h2>
			<p class="text-lg text-slate-400 mb-8 md:mb-10 max-w-2xl mx-auto">
				{tr('analytics_cta_desc')}
			</p>
			<MagneticButton
				href="/{lang}/#contact"
				class="btn-primary text-lg inline-flex items-center gap-3 glow-green"
			>
				<span>{tr('analytics_cta_button')}</span>
				<svg
					aria-hidden="true"
					class="w-5 h-5"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M17 8l4 4m0 0l-4 4m4-4H3"
					/>
				</svg>
			</MagneticButton>
		</ScrollReveal>
	</div>
</section>

<style>
	.aurora-container {
		position: absolute;
		inset: 0;
		overflow: hidden;
		pointer-events: none;
		mask-image: linear-gradient(to bottom, black 0%, black 70%, transparent 100%);
		-webkit-mask-image: linear-gradient(to bottom, black 0%, black 70%, transparent 100%);
	}

	.aurora-layer {
		position: absolute;
		width: 200%;
		height: 100%;
		left: -50%;
		background: linear-gradient(
			180deg,
			transparent 0%,
			rgba(34, 197, 94, 0.06) 10%,
			rgba(59, 130, 246, 0.08) 30%,
			rgba(139, 92, 246, 0.06) 50%,
			rgba(34, 197, 94, 0.04) 70%,
			transparent 100%
		);
		filter: blur(80px);
		transform-origin: top center;
	}

	.aurora-1 {
		animation: aurora-wave 18s ease-in-out infinite;
		opacity: 0.35;
	}

	.aurora-2 {
		animation: aurora-wave 24s ease-in-out infinite reverse;
		opacity: 0.28;
		animation-delay: -10s;
	}

	.aurora-3 {
		animation: aurora-wave 21s ease-in-out infinite;
		opacity: 0.22;
		animation-delay: -6s;
		background: linear-gradient(
			180deg,
			transparent 0%,
			rgba(6, 182, 212, 0.08) 20%,
			rgba(34, 197, 94, 0.06) 40%,
			rgba(59, 130, 246, 0.04) 60%,
			transparent 100%
		);
	}

	@keyframes aurora-wave {
		0% {
			transform: translateX(-15%) skewX(-12deg) scaleY(1) rotate(-2deg);
		}
		20% {
			transform: translateX(10%) skewX(8deg) scaleY(1.2) rotate(1deg);
		}
		40% {
			transform: translateX(-5%) skewX(-15deg) scaleY(0.85) rotate(-1deg);
		}
		60% {
			transform: translateX(15%) skewX(12deg) scaleY(1.15) rotate(2deg);
		}
		80% {
			transform: translateX(-10%) skewX(-8deg) scaleY(0.95) rotate(-1.5deg);
		}
		100% {
			transform: translateX(-15%) skewX(-12deg) scaleY(1) rotate(-2deg);
		}
	}
</style>
